import { useRef, useState, useLayoutEffect, RefObject } from 'react'

type UseStickyCellMeasurementsArgs = {
  /** Indices of items (cells) that should be measured and made sticky. */
  itemIndicesToMeasure: Array<number>
  /** Total number of items (cells) in the collection to correctly size the refs array. */
  numberOfItems: number
  measurementTrigger?: any
}

type UseStickyCellMeasurementsReturn<T extends HTMLElement> = {
  /** Ref object holding an array of refs to be assigned to each item (cell) DOM element. */
  itemRefs: RefObject<Array<T | null>>
  /** A record mapping itemIndex to its calculated 'left' offset in pixels. */
  calculatedOffsets: { [key: number]: number }
}

export const useStickyCellMeasurements = <T extends HTMLElement>({
  itemIndicesToMeasure,
  numberOfItems,
  measurementTrigger,
}: UseStickyCellMeasurementsArgs): UseStickyCellMeasurementsReturn<T> => {
  const itemRefs = useRef<Array<T | null>>([])

  if (itemRefs.current.length !== numberOfItems) {
    itemRefs.current = Array(numberOfItems).fill(null)
  }

  const [calculatedOffsets, setCalculatedOffsets] = useState<{ [key: number]: number }>({})

  useLayoutEffect(() => {
    if (itemIndicesToMeasure.length > 0 && numberOfItems > 0 && itemRefs.current.length === numberOfItems) {
      const newOffsets: { [key: number]: number } = {}
      let didAnyOffsetChange = false

      const sortedIndicesToMeasure = [...itemIndicesToMeasure].sort((a, b) => a - b)

      sortedIndicesToMeasure.forEach((pinnedIndex) => {
        if (pinnedIndex < 0 || pinnedIndex >= numberOfItems) return

        let cumulativeWidth = 0
        for (let i = 0; i < pinnedIndex; i++) {
          const precedingCell = itemRefs.current[i]
          if (precedingCell) {
            cumulativeWidth += precedingCell.getBoundingClientRect().width
          }
        }
        newOffsets[pinnedIndex] = cumulativeWidth

        if (calculatedOffsets[pinnedIndex] !== cumulativeWidth) {
          didAnyOffsetChange = true
        }
      })

      if (didAnyOffsetChange || Object.keys(newOffsets).length !== Object.keys(calculatedOffsets).length) {
        setCalculatedOffsets(newOffsets)
      }
    } else if (Object.keys(calculatedOffsets).length > 0) {
      setCalculatedOffsets({})
    }
  }, [itemIndicesToMeasure, numberOfItems, measurementTrigger, calculatedOffsets])

  return { itemRefs, calculatedOffsets }
}
